import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { QRCodeSVG } from "qrcode.react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  Alert,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SecurityIcon from "@mui/icons-material/Security";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import { getUserState } from "../../Redux/Reducers/userReducer";
import {
  getUserDetails,
  associateSoftwareToken,
  verifyAndEnableSoftwareToken,
  disableSoftwareToken,
} from "../../utils/srpLogin";
import { fetchBackupCodes } from "../../utils/restAPI";

const REGION = process.env.REACT_APP_AWS_REGION;
const ACCENT = "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)";

// Small numbered badge used to lead each step.
const StepBadge = ({ n }) => (
  <Box
    sx={{
      width: 30,
      height: 30,
      borderRadius: "50%",
      background: ACCENT,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 15,
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    {n}
  </Box>
);

const StepHeader = ({ n, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
    <StepBadge n={n} />
    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "text.primary" }}>
      {title}
    </Typography>
  </Box>
);

const TwoFactorAuth = () => {
  const theme = useTheme();
  const user = useSelector(getUserState);
  const accessToken = user?.accessToken;
  const accountLabel = user?.username;

  const [status, setStatus] = useState("checking"); // checking | enrolling | enabled
  const [secretCode, setSecretCode] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);

  const softBg =
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)";

  // On mount: check whether TOTP is already enabled; if not, request a secret.
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!accessToken) return;

      try {
        const details = await getUserDetails({ region: REGION, accessToken });
        const alreadyEnabled =
          details.userMFASettingList?.includes("SOFTWARE_TOKEN_MFA");

        if (cancelled) return;

        if (alreadyEnabled) {
          setStatus("enabled");
          return;
        }

        const { secretCode, otpauthUri } = await associateSoftwareToken({
          region: REGION,
          accessToken,
          accountLabel,
        });

        if (cancelled) return;
        setSecretCode(secretCode);
        setOtpauthUri(otpauthUri);
        setStatus("enrolling");
      } catch (error) {
        if (cancelled) return;
        console.error("2FA init failed:", error);
        toast.error(error.message || "Could not start two-factor setup.");
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [accessToken, accountLabel]);

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(secretCode);
      toast.success("Setup key copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — select the key and copy it manually.");
    }
  };

  const handleEnable = async () => {
    const clean = code.trim();
    if (!/^\d{6}$/.test(clean)) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setSubmitting(true);
    try {
      await verifyAndEnableSoftwareToken({
        region: REGION,
        accessToken,
        code: clean,
      });
      
      const res = await fetchBackupCodes(accessToken);
      if (res.success && Array.isArray(res.data) && res.data.length > 0) {
        setBackupCodes(res.data);
      } else {
        console.warn("fetchBackupCodes failed or returned empty:", res);
        toast.warn("2FA enabled, but backup codes could not be generated. Contact support if this persists.");
      }
      
      setStatus("enabled");
      toast.success("Two-factor authentication is now enabled.");
    } catch (error) {
      console.error("2FA enable failed:", error);
      toast.error(error.message || "Could not enable two-factor authentication.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Are you sure you want to disable two-factor authentication? This will make your account less secure.")) return;
    setSubmitting(true);
    try {
      await disableSoftwareToken({ region: REGION, accessToken });
      toast.success("Two-factor authentication has been disabled.");
      setStatus("checking"); // setting to checking will trigger useEffect to re-initialize
      setBackupCodes([]);
    } catch (error) {
      toast.error(error.message || "Failed to disable two-factor authentication.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        mb: 12,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        flex: 1,
      }}
    >
      {/* Header Section with Icon, Heading, and Subheading */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
            <SecurityIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.2 }}>
              Two-Factor Authentication
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Form Content */}
      <Box sx={{ maxWidth: 560 }}>
        {status === "checking" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {status === "enabled" && (
          <Box>
            <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Two-factor authentication is enabled
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                Next time you sign in, we'll ask for the 6-digit code from your authenticator app.
              </Typography>
            </Alert>

            {backupCodes.length > 0 && (
              <Box sx={{ mb: 4, p: 2, bgcolor: softBg, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <Typography variant="h6" sx={{ mb: 1, fontSize: "1rem", fontWeight: 700 }}>
                  Your Backup Codes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Save these codes in a secure place. Each code can only be used once to log in if you lose access to your authenticator app.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {backupCodes.map((bc, i) => (
                    <Typography key={i} sx={{ fontFamily: 'monospace', fontWeight: 600, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                      {bc}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <Button
              variant="outlined"
              color="error"
              onClick={handleDisable}
              disabled={submitting}
            >
              Disable Two-Factor Authentication
            </Button>
          </Box>
        )}

        {status === "enrolling" && (
          <>
            {/* Step 1 — get an app */}
            <StepHeader n={1} title="Get an authenticator app" />
            <Box
              sx={{
                bgcolor: softBg,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <SmartphoneIcon sx={{ color: "text.secondary", mt: 0.3 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 600, mb: 0.5 }}>
                    New to this? You'll need a free app on your phone.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    An authenticator app generates the security codes. Install{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                      Google Authenticator
                    </Box>
                    ,{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                      Microsoft Authenticator
                    </Box>{" "}
                    or{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                      Authy
                    </Box>
                    , then choose "Add account" → "Scan QR code".
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Step 2 — scan */}
            <StepHeader n={2} title="Scan the QR code" />
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                }}
              >
                {otpauthUri && <QRCodeSVG value={otpauthUri} size={188} />}
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 1 }}>
              Can't scan it? Enter this setup key by hand instead:
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: softBg,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                px: 2,
                py: 1,
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  letterSpacing: 1,
                  wordBreak: "break-all",
                  color: "text.primary",
                }}
              >
                {secretCode}
              </Typography>
              <Tooltip title="Copy key">
                <IconButton size="small" onClick={handleCopyKey}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Step 3 — enter code */}
            <StepHeader n={3} title="Enter the 6-digit code" />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Type the current code shown in your authenticator app.
            </Typography>
            <TextField
              fullWidth
              label="Authentication code"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleEnable();
                }
              }}
              inputProps={{
                inputMode: "numeric",
                maxLength: 6,
                style: { letterSpacing: 6, fontSize: "1.15rem", fontWeight: 600 },
              }}
              sx={{ mb: 3 }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={submitting || code.length !== 6}
              onClick={handleEnable}
              sx={{ height: 52, fontWeight: 700, borderRadius: 2, textTransform: "none", fontSize: "1rem" }}
            >
              {submitting ? "Enabling…" : "Enable two-factor authentication"}
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default TwoFactorAuth;
